import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { SessionService } from '../../../components/services/app-state-services/session/session.service';
import { IframeModeService } from '../../../components/services/iframe-mode/iframe-mode.service';
import { environment } from '../../../../environments/environment';

interface TenantResolveResponse {
  tenantCode?: string;
}

/**
 * Resolves tenant code before anonymous widget API calls (app_configs, chat, STOMP).
 *
 * Embedded (WIDGET/INLINE): resolve API (iframe host) runs first and is authoritative.
 *
 * Full app priority: session → ?tenantCode= → resolve API → defaultTenantCode.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantResolveService {
  private resolveInFlight: Observable<string | null> | null = null;

  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private iframeModeService: IframeModeService
  ) {}

  ensureTenantCode(): Observable<string | null> {
    if (this.iframeModeService.isIframeMode()) {
      const stored = this.sessionService.getTenantCode()?.trim();
      if (stored) {
        return of(stored);
      }
      return this.resolveViaHost();
    }

    const fromSession = this.sessionService.getTenantCode()?.trim();
    if (fromSession) {
      return of(fromSession);
    }

    try {
      const fromQuery = new URLSearchParams(window.location.search).get('tenantCode')?.trim();
      if (fromQuery) {
        this.sessionService.setTenantCode(fromQuery);
        return of(fromQuery);
      }
    } catch {
      // continue
    }

    const defaultTenant = (environment as { defaultTenantCode?: string }).defaultTenantCode?.trim();
    if (defaultTenant) {
      this.sessionService.setTenantCode(defaultTenant);
      return of(defaultTenant);
    }

    return this.resolveViaHost();
  }

  private resolveViaHost(): Observable<string | null> {
    if (!this.resolveInFlight) {
      this.resolveInFlight = this.resolveFromHost(window.location.hostname).pipe(
        map((code) => {
          this.sessionService.setTenantCode(code ?? '');
          return code;
        }),
        catchError((err) => {
          console.warn('[tenant] host resolve failed:', err);
          return of(null);
        }),
        finalize(() => {
          this.resolveInFlight = null;
        }),
        shareReplay(1)
      );
    }
    return this.resolveInFlight;
  }

  private resolveFromHost(host: string): Observable<string | null> {
    const url = `api/tenant/resolve?host=${encodeURIComponent(host)}`;
    return this.http.get<TenantResolveResponse>(url).pipe(
      map((body) => body?.tenantCode?.trim() || null),
      catchError((err) => {
        if (err?.status === 404) {
          return of(null);
        }
        throw err;
      })
    );
  }
}
